import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Spinner,
  VStack,
  useToast,
} from '@chakra-ui/react';

const LOAD_SCHOOLS = gql`
  query fetchSchools($eventId: ID!) {
    fetchSchools(tenantId: 1) {
      id
      name
      status
    }
    eventTeams(eventId: $eventId) {
      id
      name
      status
    }
  }
`;


const ADD_SCHOOLS_TO_COALITION = gql`
mutation addSchoolsToCoalition($id: ID!, $schoolIds: [Int!]!) {
  addSchoolsToCoalition(input: {
    id: $id,
    schoolIds: $schoolIds,
  }) {
    coalition {
      id
      status
      tenantId
      code
      fetchEventCompetition {
        id
        title
      }
      fetchCoalitionsSchools{
        id
        status
        schoolId
        tenantId
        coalitionId
        fetchSchool {
          id
          name
        }
        fetchCoalitionMembers {
          id
          user {
            id
            code
            firstname
            lastname
          }
        }
      }
    }
  }
}
`;

const useSchools = (eventId, schoolIdsInCoalition) => {
  const { data, loading } = useQuery(LOAD_SCHOOLS, {
    variables: {
      eventId,
    },
  });

  if (loading) {
    return { loading };
  }
  const schoolsInEvent = data.eventTeams.filter(
    (school) => !schoolIdsInCoalition.includes(parseInt(school.id, 10)),
  );
  schoolsInEvent.sort((s1, s2) => s1.name.localeCompare(s2.name));

  return {
    loading: false,
    schoolsInEvent,
  };
};

const AddSchoolModalContent = ({
  loading,
  schoolsInEvent,
  selectedSchoolIds,
  onSelect,
}) => {
  if (loading) {
    return <Spinner />;
  }
  if (schoolsInEvent.length === 0) {
    return <p>no schools found</p>;
  }

  return (
    <Flex
      direction="column"
      style={{
        overflow: 'scroll',
        maxHeight: '400px',
        width: '100%',
        padding: '8px',
      }}
    >
      {schoolsInEvent.map((school) => (
        <Flex key={school.id}>
          <Checkbox
            isChecked={selectedSchoolIds.includes(school.id)}
            onChange={() => onSelect(school.id)}
          >
            {school.name}
          </Checkbox>
        </Flex>
      ))}
    </Flex>
  );
};
AddSchoolModalContent.propTypes = {
  loading: PropTypes.bool.isRequired,
  schoolsInEvent: PropTypes.arrayOf({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  selectedSchoolIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSelect: PropTypes.func.isRequired,
};

export const AddSchoolModal = ({
  isOpen,
  onClose,
  eventId,
  coalitionId,
  schoolIdsInCoalition,
}) => {
  const toast = useToast();
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
  const toggle = (schoolId) => {
    if (selectedSchoolIds.includes(schoolId)) {
      setSelectedSchoolIds(selectedSchoolIds.filter((id) => id !== schoolId));
    } else {
      setSelectedSchoolIds(selectedSchoolIds.concat(schoolId));
    }
  };

  const {
    schoolsInEvent,
    loading,
  } = useSchools(eventId, schoolIdsInCoalition);

  const [addSchoolsToCoalition, { loading: addSchoolLoading }] = useMutation(
    ADD_SCHOOLS_TO_COALITION,
  );
  const onConfirm = () => {
    addSchoolsToCoalition({
      variables: {
        id: coalitionId,
        schoolIds: selectedSchoolIds.map((id) => parseInt(id, 10)),
      },
    }).then(() => {
      toast({
        title: 'Successfully added schools to coalition',
        status: 'success',
        isClosable: true,
        position: 'top',
      });

      onClose();
    }).catch((e) => {
      toast({
        title: e.graphQLErrors?.[0]?.message ?? e.message,
        status: 'error',
        isClosable: true,
        position: 'top',
      });
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Schools to Coalition</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <AddSchoolModalContent
              loading={loading}
              schoolsInEvent={schoolsInEvent}
              selectedSchoolIds={selectedSchoolIds}
              onSelect={toggle}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onConfirm} isLoading={addSchoolLoading} disabled={selectedSchoolIds.length === 0}>
            Add Schools
          </Button>
          <Button colorScheme="gray" variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AddSchoolModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.number.isRequired,
  coalitionId: PropTypes.number.isRequired,
  schoolIdsInCoalition: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
};
